"use client";
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';

/* multicolour Google "G" mark, used on every search-pill and the mock search bar */
function GoogleG({ size = 16 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14.2-5.1l-6.6-5.4C29.6 35.4 27 36 24 36c-5.3 0-9.7-3.1-11.3-7.8l-6.6 5.1C9.6 39.7 16.3 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.6 5.4C41.4 35.7 44 30.5 44 24c0-1.2-.1-2.4-.4-3.5z" />
        </svg>
    );
}

function MicIcon({ size = 14 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="9" y="2" width="6" height="12" rx="3" fill="#4285F4" />
            <path d="M5 11a7 7 0 0014 0" stroke="#34A853" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M12 18v3" stroke="#EA4335" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M9 21h6" stroke="#FBBC05" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

interface SearchPill {
    label: string;
    style: React.CSSProperties;
    delay: number;
    duration: number;
}

const pills: SearchPill[] = [
    { label: 'Blog content not ranking', style: { top: '6%', left: '0%' }, delay: 0.2, duration: 4.2 },
    { label: 'High bounce, low dwell time', style: { top: '38%', left: '-3%' }, delay: 0.6, duration: 4.8 },
    { label: "Content ideas that don't convert", style: { top: '70%', left: '2%' }, delay: 1.0, duration: 4.4 },
    { label: 'Skipped by AI Overviews', style: { top: '8%', right: '0%' }, delay: 0.35, duration: 4.6 },
    { label: 'Thin content flagged by Google', style: { top: '40%', right: '-3%' }, delay: 0.75, duration: 4.0 },
    { label: 'No topical authority in niche', style: { top: '72%', right: '1%' }, delay: 1.15, duration: 4.6 },
];

function FloatingPill({ pill, index }: { pill: SearchPill; index: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            animate={{ opacity: 1, y: [0, -8, 0], scale: 1 }}
            transition={{
                opacity: { duration: 0.5, delay: pill.delay },
                scale: { duration: 0.5, delay: pill.delay },
                y: { duration: pill.duration, delay: pill.delay + 0.5, repeat: Infinity, ease: 'easeInOut' },
            }}
            className="hidden lg:flex absolute items-center gap-2 pl-3 pr-3.5 py-2 rounded-full bg-white"
            style={{
                ...pill.style,
                boxShadow: '0 12px 28px -10px rgba(8,4,0,0.18)',
                border: '1px solid rgba(8,4,0,0.06)',
                zIndex: index,
            }}
        >
            <GoogleG size={15} />
            <span className="text-xs font-medium whitespace-nowrap" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.75)' }}>
                {pill.label}
            </span>
            <MicIcon size={13} />
        </motion.div>
    );
}

/* concentric arcs radiating from the headline, echoing a search "ripple" */
function ArcBackground() {
    const radii = [140, 210, 280, 350, 420];
    return (
        <svg
            className="absolute left-1/2 pointer-events-none"
            style={{ top: -40, transform: 'translateX(-50%)' }}
            width="1100"
            height="480"
            viewBox="0 0 1100 480"
            fill="none"
            aria-hidden="true"
        >
            {radii.map((r, i) => (
                <circle
                    key={r}
                    cx="550"
                    cy="0"
                    r={r}
                    stroke={i % 2 === 0 ? PURPLE : LIME}
                    strokeOpacity={0.14 - i * 0.015}
                    strokeWidth="1.4"
                    fill="none"
                />
            ))}
        </svg>
    );
}

function MockSearchBar() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex items-center gap-3 rounded-full pl-5 pr-4 py-3.5 w-full max-w-md"
            style={{ background: INK }}
        >
            <GoogleG size={18} />
            <span className="flex-1 text-left text-sm md:text-base" style={{ fontFamily: MANROPE, color: '#fbfdfd' }}>
                content that ranks{' '}
                <motion.span
                    aria-hidden="true"
                    className="inline-block w-[1.5px] h-4 align-middle"
                    style={{ background: LIME }}
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                />
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke={LIME} strokeWidth="2" />
                <path d="M20 20l-4.5-4.5" stroke={LIME} strokeWidth="2" strokeLinecap="round" />
            </svg>
        </motion.div>
    );
}

export default function ContentStrategyHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
                style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
            >
                {/* dot grid texture, matches the dark sections below */}
                <div
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
                />

                <ArcBackground />

                <div className="relative max-w-[1400px] mx-auto">

                    {/* floating google-style search pills, desktop only */}
                    {pills.map((p, i) => (
                        <FloatingPill key={p.label} pill={p} index={i} />
                    ))}

                    {/* centred column */}
                    <div className="relative z-10 max-w-[900px] mx-auto flex flex-col items-center text-center gap-6">

                        {/* Breadcrumb */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-2 text-xs md:text-sm font-medium"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}
                        >
                            <Link href="/services/search-visibility" className="hover:underline" style={{ color: 'rgba(8,4,0,0.45)' }}>
                                SEO Services
                            </Link>
                            <span>/</span>
                            <span style={{ color: PURPLE }}>SEO Content Strategy</span>
                        </motion.div>

                        {/* H1 */}
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-4xl md:text-6xl lg:text-[3.6rem] font-normal leading-[1.08] tracking-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            SEO Content Strategy Built Around{' '}
                            <span style={{ color: PURPLE }}>Search Intent</span>
                            , Expertise &amp; Business Growth
                        </motion.h1>

                        {/* Description */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.22 }}
                            className="flex flex-col gap-3 max-w-2xl"
                        >
                            <p className="text-sm md:text-base leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.65)', letterSpacing: '0.01em' }}>
                                Publishing more content doesn&apos;t automatically build organic visibility. Lead Workz builds SEO content strategies around what your audience actually searches for, what your business can credibly speak about, and where content can contribute to commercial growth.
                            </p>
                        </motion.div>

                        {/* CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.34 }}
                            className="pt-1"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center justify-center px-7 py-3.5 rounded-full font-semibold text-sm md:text-base transition-all duration-300 hover:scale-105"
                                style={{ background: PURPLE, color: '#ffffff', fontFamily: CLASH, letterSpacing: '0.08em' }}
                            >
                                Build My Content Strategy →
                            </button>
                        </motion.div>

                        {/* mock search bar */}
                        <MockSearchBar />
                    </div>
                </div>
            </div>
        </section>
    );
}
